// In-page QR reader. Used by New Trainee and Field Access.
// Opens the back camera in a <video>, reads frames with jsQR, and hands each
// decoded link to the caller. Stops on its own when the video leaves the page.

import jsQR from "jsqr";

export interface QrScanner {
  stop(): void;
}

/**
 * Starts the camera. `onLink` gets every decoded QR; return true to stop.
 * `onStatus` gets the plain-word line to show over the camera.
 */
export function startQrScanner(video: HTMLVideoElement, onLink: (link: string) => boolean, onStatus: (text: string) => void): QrScanner {
  let stream: MediaStream | null = null;
  let stopped = false;

  const stop = () => {
    stopped = true;
    stream?.getTracks().forEach((t) => t.stop());
    stream = null;
  };

  void (async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      onStatus("This browser cannot open the camera.");
      return;
    }
    onStatus("Starting the camera…");
    try {
      stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
    } catch {
      onStatus("Camera blocked. Allow the camera in the browser settings.");
      return;
    }
    if (stopped || !video.isConnected) {
      stop();
      return;
    }
    video.srcObject = stream;
    await video.play().catch(() => undefined);
    onStatus("Looking for a SafeTag QR…");
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const tick = () => {
      if (stopped || !video.isConnected) {
        stop();
        return;
      }
      if (ctx && video.readyState >= 2 && video.videoWidth) {
        const scale = Math.min(1, 640 / video.videoWidth);
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(img.data, img.width, img.height, { inversionAttempts: "dontInvert" });
        if (code?.data && onLink(code.data)) {
          stop();
          return;
        }
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  })();

  return { stop };
}
